import { MutableRefObject, useCallback, useRef, useState } from 'react';

type StateUpdater<T> = (oldValue: T) => T;
type SetStateValue<T> = Exclude<T, StateUpdater<T>> | StateUpdater<T>;
type SetState<T> = (value: SetStateValue<T>) => void;

export type UseStateWithRefResult<T> = [T, SetState<T>, MutableRefObject<T>];

function isStateUpdater<T>(val: SetStateValue<T>): val is StateUpdater<T> {
  return typeof val === 'function';
}

export default function useStateWithRef<T>(
  initial: T,
): UseStateWithRefResult<T> {
  const [value, setValue] = useState(initial);
  const ref = useRef<T>(initial);

  const handleSetValue: SetState<T> = useCallback((valOrFn) => {
    if (isStateUpdater(valOrFn)) {
      setValue((oldVal) => {
        const newVal = valOrFn(oldVal);
        ref.current = newVal;
        return newVal;
      });
    } else {
      ref.current = valOrFn;
      setValue(valOrFn);
    }
  }, []);

  return [value, handleSetValue, ref];
}
